import React, { useState, useEffect } from 'react';
import apiService from '../services/api'; 
import { useAuth } from '../contexts/AuthContext';
import { Card, Col, Row, Form, Button, Alert, Spinner, ListGroup, Container } from 'react-bootstrap';

// Interface pour typer les utilisateurs (à adapter selon votre modèle User)
interface User {
  // Utiliser 'id: number' car c'est ce que renvoie PostgreSQL
  id: number; 
  username: string;
  email: string;
  // Ajoutez d'autres champs si nécessaire (role, etc.)
}

const ProfilePage: React.FC = () => {
  const { user } = useAuth(); // Suppression de logout car non utilisé
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // États pour le formulaire de création d'utilisateur
  const [showCreateUserForm, setShowCreateUserForm] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserConfirmPassword, setNewUserConfirmPassword] = useState('');
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [createUserError, setCreateUserError] = useState<string | null>(null);
  const [createUserSuccess, setCreateUserSuccess] = useState<string | null>(null);

  // États pour la liste des utilisateurs
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletingUser, setDeletingUser] = useState<string | null>(null);

  // Fonction pour charger les utilisateurs
  const fetchUsers = async () => {
    setLoadingUsers(true);
    setDeleteError(null);
    try {
      const response = await apiService.get<User[]>('/api/auth/users'); // Assurez-vous que cette route existe
      setUsers(response.data);
    } catch (err) {
      console.error("Erreur lors de la récupération des utilisateurs:", err);
      setError('Impossible de charger la liste des utilisateurs.'); // Utiliser l'état d'erreur général ou un état dédié
    } finally {
      setLoadingUsers(false);
    }
  };

  // Effet pour charger les utilisateurs au montage et après création/suppression
  useEffect(() => {
    fetchUsers();
  }, []); // Se charge une seule fois au montage pour l'instant

  // Effet pour réinitialiser les messages d'erreur/succès lors du changement de formulaire
  useEffect(() => {
    setError(null);
    setSuccess(null);
    setCreateUserError(null);
    setCreateUserSuccess(null);

    // Réinitialiser aussi les champs lors du changement de vue
    setCurrentPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setNewUsername('');
    setNewUserEmail('');
    setNewUserPassword('');
    setNewUserConfirmPassword('');
    
  }, [showCreateUserForm]);

  const handleSubmitPasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (newPassword !== confirmNewPassword) {
      setError('Les nouveaux mots de passe ne correspondent pas.');
      return;
    }

    if (newPassword.length < 6) {
      setError('Le nouveau mot de passe doit contenir au moins 6 caractères.');
      return;
    }

    setIsLoading(true);
    try {
      await apiService.put('/api/auth/password', {
        currentPassword,
        newPassword,
      });

      setSuccess('Mot de passe mis à jour avec succès !');
      // Réinitialiser les champs
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setError(null); // Assurez-vous qu'il n'y a pas d'erreur affichée
    } catch (err: any) {
      console.error("Erreur lors du changement de mot de passe:", err);
      const errorMessage = err.response?.data?.error || 'Une erreur est survenue lors du changement de mot de passe.';
      setError(errorMessage);
      setSuccess(null); // Assurez-vous qu'il n'y a pas de message de succès affiché
    } finally {
      setIsLoading(false);
    }
  };

  // Gestionnaire de soumission pour la création d'utilisateur
  const handleCreateUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateUserError(null);
    setCreateUserSuccess(null);

    if (newUserPassword !== newUserConfirmPassword) {
      setCreateUserError('Les mots de passe ne correspondent pas.');
      return;
    }

    if (newUserPassword.length < 6) {
      setCreateUserError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }

    setIsCreatingUser(true);
    try {
      const response = await apiService.post('/api/auth/users', {
        username: newUsername,
        email: newUserEmail,
        password: newUserPassword,
      });

      setCreateUserSuccess(response.data.message || 'Utilisateur créé avec succès !');
      // Réinitialiser les champs du formulaire de création
      setNewUsername('');
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserConfirmPassword('');
      setCreateUserError(null); // Assurez-vous qu'il n'y a pas d'erreur affichée
      fetchUsers(); // Recharger la liste des utilisateurs après création
      // Optionnel: Masquer le formulaire après succès si on le souhaite
      // setTimeout(() => setShowCreateUserForm(false), 2000); // Masque après 2s
    } catch (err: any) {
      console.error("Erreur lors de la création de l'utilisateur:", err);
      const errorMessage = err.response?.data?.error || 'Une erreur est survenue lors de la création de l\'utilisateur.';
      setCreateUserError(errorMessage);
      setCreateUserSuccess(null); // Assurez-vous qu'il n'y a pas de message de succès affiché
    } finally {
      setIsCreatingUser(false);
    }
  };

  // Gestionnaire de suppression d'utilisateur
  const handleDeleteUser = async (userId: number) => {
    // Confirmation avant suppression (recommandé)
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.')) {
      return;
    }

    setDeleteError(null);
    setDeletingUser(String(userId)); // Stocker l'ID comme string pour l'état
    try {
      await apiService.delete(`/api/auth/users/${userId}`); // L'ID sera correctement passé dans l'URL
      // Recharger la liste des utilisateurs après suppression
      fetchUsers(); 
    } catch (err: any) {
      console.error("Erreur lors de la suppression de l'utilisateur:", err);
      const errorMessage = err.response?.data?.error || 'Une erreur est survenue lors de la suppression.';
      setDeleteError(errorMessage);
    } finally {
      setDeletingUser(null);
    }
  };

  return (
    <Container className="mt-5">
      <Row>
        {/* Colonne de gauche : Formulaires Profil / Création */}
        <Col md={6} className="mb-3 mb-md-0"> 
          <Card className="h-100"> 
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{showCreateUserForm ? 'Créer un Nouvel Utilisateur' : 'Mon Profil'}</h5>
              <Button 
                variant={showCreateUserForm ? "outline-secondary" : "outline-primary"}
                onClick={() => setShowCreateUserForm(!showCreateUserForm)}
              >
                {showCreateUserForm ? 'Voir Mon Profil' : 'Créer un utilisateur'}
              </Button>
            </Card.Header>
            
            <Card.Body>
              {/* Affichage conditionnel des formulaires */}
              {showCreateUserForm ? (
                // FORMULAIRE DE CRÉATION D'UTILISATEUR
                <>
                  {createUserError && <Alert variant="danger">{createUserError}</Alert>}
                  {createUserSuccess && <Alert variant="success">{createUserSuccess}</Alert>}
                  
                  <Form onSubmit={handleCreateUserSubmit}>
                    <Form.Group className="mb-3" controlId="newUsername">
                      <Form.Label>Nom d'utilisateur</Form.Label>
                      <Form.Control
                        type="text"
                        value={newUsername}
                        onChange={(e) => setNewUsername(e.target.value)}
                        required
                        disabled={isCreatingUser}
                        autoComplete="username"
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3" controlId="newUserEmail">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        value={newUserEmail}
                        onChange={(e) => setNewUserEmail(e.target.value)}
                        required
                        disabled={isCreatingUser}
                        autoComplete="email"
                      />
                    </Form.Group>
                    
                    <Form.Group className="mb-3" controlId="newUserPassword">
                      <Form.Label>Mot de passe</Form.Label>
                      <Form.Control
                        type="password"
                        value={newUserPassword}
                        onChange={(e) => setNewUserPassword(e.target.value)}
                        required
                        disabled={isCreatingUser}
                        autoComplete="new-password"
                      />
                      <Form.Text className="text-muted">
                        Minimum 6 caractères.
                      </Form.Text>
                    </Form.Group>
                    
                    <Form.Group className="mb-3" controlId="newUserConfirmPassword">
                      <Form.Label>Confirmer le mot de passe</Form.Label>
                      <Form.Control
                        type="password"
                        value={newUserConfirmPassword}
                        onChange={(e) => setNewUserConfirmPassword(e.target.value)}
                        required
                        disabled={isCreatingUser}
                        autoComplete="new-password"
                      />
                    </Form.Group>
                    
                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100"
                      disabled={isCreatingUser}
                    >
                      {isCreatingUser ? (
                        <>
                          <Spinner animation="border" size="sm" role="status" className="me-2" />
                          Création...
                        </>
                      ) : (
                        'Créer l\'utilisateur'
                      )}
                    </Button>
                  </Form>
                </>
              ) : (
                // FORMULAIRE DE CHANGEMENT DE MOT DE PASSE
                <>
                  {error && <Alert variant="danger">{error}</Alert>}
                  {success && <Alert variant="success">{success}</Alert>}
      
                  <Form onSubmit={handleSubmitPasswordChange}>
                    <Form.Group className="mb-3" controlId="currentPassword">
                      <Form.Label>Mot de passe actuel</Form.Label>
                      <Form.Control
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                        disabled={isLoading}
                        autoComplete="current-password"
                      />
                    </Form.Group>
      
                    <Form.Group className="mb-3" controlId="newPassword">
                      <Form.Label>Nouveau mot de passe</Form.Label>
                      <Form.Control
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        disabled={isLoading}
                        autoComplete="new-password"
                      />
                      <Form.Text className="text-muted">
                        Minimum 6 caractères.
                      </Form.Text>
                    </Form.Group>
      
                    <Form.Group className="mb-3" controlId="confirmNewPassword">
                      <Form.Label>Confirmer le nouveau mot de passe</Form.Label>
                      <Form.Control
                        type="password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        required
                        disabled={isLoading}
                        autoComplete="new-password"
                      />
                    </Form.Group>
      
                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Spinner animation="border" size="sm" role="status" className="me-2" />
                          Mise à jour...
                        </>
                      ) : (
                        'Changer le mot de passe'
                      )}
                    </Button>
                  </Form>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Colonne de droite : Liste des utilisateurs */}
        <Col md={6}>
          <Card className="h-100"> 
            <Card.Header as="h5">Liste des utilisateurs</Card.Header> 
            <Card.Body>
              {loadingUsers ? (
                <div className="text-center">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Chargement...</span>
                  </Spinner>
                </div>
              ) : deleteError ? (
                <Alert variant="danger">{deleteError}</Alert>
              ) : (
                <ListGroup>
                  {users.length === 0 ? (
                    <ListGroup.Item>Aucun utilisateur trouvé.</ListGroup.Item>
                  ) : (
                    users.map((userItem) => (
                      <ListGroup.Item key={userItem.id} className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{userItem.username}</strong> ({userItem.email})
                          {/* Vérifier si l'utilisateur courant est l'utilisateur connecté */}
                          {user && userItem.id === user.id && <span className="ms-2">(Vous)</span>}
                        </div>
                        {/* Ne pas permettre de supprimer son propre compte */}
                        {user && userItem.id !== user.id && (
                           deletingUser === String(userItem.id) ? (
                              <Spinner animation="border" size="sm" role="status">
                                <span className="visually-hidden">Suppression...</span>
                              </Spinner>
                            ) : (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteUser(userItem.id)}
                                disabled={deletingUser !== null} // Désactive si une suppression est en cours
                              >
                                Supprimer
                              </Button>
                            )
                        )}
                      </ListGroup.Item>
                    ))
                  )}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ProfilePage;
